import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Handwriting-style fonts each mixtape picks from. Sourced from
// public.mixtape_fonts so admins can later toggle/add/remove without
// a frontend deploy. Active rows are dynamically pulled into the
// document via a single <link> tag (see useMixtapeFontsLoader).
export interface MixtapeFont {
  id:                 string;
  display_name:       string;
  /** Direct CSS font-family value, e.g. '"Rock Salt", cursive'. */
  css_family:         string;
  /** Fragment appended to https://fonts.googleapis.com/css2?. */
  google_fonts_query: string;
  /** Display size multiplier so each face renders at a comparable visual
   *  weight (some hand fonts are naturally tiny, others bulky). */
  scale:              number;
  /** Tuned line-height per face so vertical centring stays consistent. */
  line_height:        number;
  sort_index:         number;
}

export const MIXTAPE_FONTS_QUERY_KEY = ['mixtape-fonts'] as const;

/** Fallback face used when a mixtape has no font set or the pool hasn't
 *  loaded yet. Matches the previous .vhs-label styling so existing
 *  behaviour stays consistent on first paint. */
export const MIXTAPE_FONT_FALLBACK: MixtapeFont = {
  id:                 '',
  display_name:       'Default',
  css_family:         '"DM Sans", ui-sans-serif, system-ui, sans-serif',
  google_fonts_query: '',
  scale:              1.0,
  line_height:        1.0,
  sort_index:         0,
};

export function useMixtapeFonts() {
  return useQuery<MixtapeFont[]>({
    queryKey: MIXTAPE_FONTS_QUERY_KEY,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_mixtape_fonts');
      if (error) throw error;
      return (data ?? []) as MixtapeFont[];
    },
  });
}

/** id → font lookup for fast access in row renderers. Memoised on the
 *  query data so the identity is stable across renders. */
export function useMixtapeFontsMap(): Map<string, MixtapeFont> {
  const q = useMixtapeFonts();
  return useMemo(() => {
    const m = new Map<string, MixtapeFont>();
    for (const f of q.data ?? []) m.set(f.id, f);
    return m;
  }, [q.data]);
}

export function fontForMixtape(
  fontId: string | null | undefined,
  pool: Map<string, MixtapeFont>,
): MixtapeFont {
  if (!fontId) return MIXTAPE_FONT_FALLBACK;
  return pool.get(fontId) ?? MIXTAPE_FONT_FALLBACK;
}

/** Picks the "smartest" default font for a new mixtape: the one least
 *  used by `recentlyUsedFontIds` (the last N persisted fonts in the
 *  list). Guarantees the new mixtape doesn't share a font with its
 *  immediate neighbours when there's room in the pool. */
export function pickDefaultFont(
  fonts: MixtapeFont[],
  recentlyUsedFontIds: Array<string | null>,
): MixtapeFont | null {
  if (fonts.length === 0) return null;
  const recent = new Set(recentlyUsedFontIds.filter(Boolean));
  const unused = fonts.filter((f) => !recent.has(f.id));
  if (unused.length > 0) {
    return unused[Math.floor(Math.random() * unused.length)];
  }
  // All fonts have been used recently — just pick a random one and
  // accept the duplicate.
  return fonts[Math.floor(Math.random() * fonts.length)];
}

/** Mounts a single <link> tag in <head> with all active fonts so faces
 *  added to public.mixtape_fonts after deploy load automatically. The
 *  seed 7 are already in index.html for FOUT prevention on first paint;
 *  this hook covers anything added later. */
export function useMixtapeFontsLoader(): void {
  const { data } = useMixtapeFonts();
  useEffect(() => {
    if (!data || data.length === 0) return;
    const id   = 'mixtape-fonts-link';
    const href = 'https://fonts.googleapis.com/css2?'
      + data.map((f) => f.google_fonts_query).join('&')
      + '&display=swap';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id  = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [data]);
}
